/**
 * PMGT FE-2 — EDIT-mode orchestrator for the unified `ProjectWizard`.
 *
 * Mounts on the project detail page (`/projects/:projectId`). Renders the SAME
 * four-step flow as create — Basics → Parties → Parks & turbines → Overview —
 * over a LIVE project:
 *
 *   - **Basics** — base fields prefilled from `GET /projects/{id}` (project
 *     name / dates / offer ref / report language). Save → `PATCH /projects/{id}`
 *     (base fields) → re-fetch detail. The AI-models panel (its own PUT) sits
 *     as a sub-section here (reuses `AiModelsPanel`).
 *   - **Parties** — reuses the live `AccessGrantPanel` (party pickers + grants).
 *   - **Parks & turbines** — the live parks→turbines tree with per-row REMOVE
 *     (turbine → `DELETE /contract-positions/{id}`; park → `DELETE
 *     /contracts/{id}`), each confirm-dialogged, with friendly inline 409
 *     messages. ADD park/turbine reuses the create draft rows + the FE-1
 *     find-or-create + `createContract` / `createContractPosition` flow.
 *   - **Overview** — the Review step = the status-coloured `ProjectMap`
 *     (FB-14 + FE-1) followed by the read-only `ProjectEntityTree` (PEDIT-TREE:
 *     Project → Contract → Position → Inspection Instruction → Data Acquisition,
 *     linked client-side by parent FK) for the live project. The old
 *     parks→turbines list here (which duplicated the Parks & Turbines step) is
 *     replaced by that full entity tree.
 *
 * Edit-only hooks live ONLY in this component (never mounted in create mode),
 * so the create tests' partial hook mocks stay valid.
 */
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useAuthStore } from "@aeriosiq/auth-client";
import { useToast } from "../../account/ToastContext.js";
import { ConfirmDialog } from "../../admin/shared/ConfirmDialog.js";
import { useKnownTenantsQuery } from "../access/accessHooks.js";
import { AccessGrantPanel } from "../access/AccessGrantPanel.js";
import { useOwnerMemberList } from "../access/useOwnerMemberList.js";
import { ProjectEntityTree } from "../detail/ProjectEntityTree.js";
import { AiModelsPanel } from "../detail/AiModelsPanel.js";
import { WorkflowPanel } from "../detail/WorkflowPanel.js";
import { ContractPositionStatusChip } from "../../contract-positions/ContractPositionStatusChip.js";
import { useContractPositionsListQuery } from "../../contract-positions/useContractPositions.js";
import { useCreateContractPositionMutation } from "../../contract-positions/useContractPositions.js";
import { useDeleteContractPositionMutation } from "../../contract-positions/useContractPositions.js";
import { useCreateContractMutation } from "../../contracts/useContracts.js";
import { useDeleteContractMutation } from "../../contracts/useContracts.js";
import { useUpdateContractMutation } from "../../contracts/useContracts.js";
import type { ContractUpdateRequest } from "../../contracts/types.js";
import { useContractsByProjectQuery } from "../../contract-positions/useContractPositions.js";
import { useTenantSettingsQuery } from "../../tenancy/hooks.js";
import type { KnownTenant } from "../../tenancy/types.js";
import { useCreateSiteMutation } from "../../sites/queries/hooks.js";
import type { CreateSiteBody } from "../../sites/queries/types.js";
import {
  useCreateTurbineMutation,
  type CreateTurbineBody,
} from "../../turbines/queries/index.js";
import type {
  ContractPositionListItem,
  ContractPositionState,
} from "../../contract-positions/types.js";
import type {
  ListProjectsParams,
  ProjectsConflictReason,
  UpdateProjectRequest,
} from "../types.js";
import {
  useProjectDetailQuery,
  useUpdateProjectMutation,
} from "../useProjects.js";
import { WizardShell } from "./WizardShell.js";
import { BasicsStep } from "./WizardSteps.js";
import {
  EDIT_STEP_LABELS,
  EMPTY_PARTY,
  ContractPartyField,
  EditPartyField,
  EditReportingPartyField,
  ParkRow,
  TurbineRow,
  contractPartyFromIds,
  partyValueFromIds,
  hasAnyError,
  nextKey,
  stepsWithErrors,
  validateWizard,
  type ContractPartyValue,
  type DraftPark,
  type DraftTurbine,
  type PartyValue,
  type PrefilledTenant,
  type ProjectsFetchErrorLike,
} from "./wizardParts.js";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  PlusIcon,
  Trash2Icon,
} from "@aeriosiq/ui/icons";

// Lazy-load the heavy map (MapLibre + PMTiles) — mirrors the detail page.
const ProjectMap = lazy(() =>
  import("../detail/ProjectMap.js").then((m) => ({ default: m.ProjectMap })),
);

const POSITIONS_PAGE_SIZE = 500;

// PEDIT-PARTIES — the same wide own+member sweep the detail page uses to resolve
// a row's party tenant NAMES (the detail endpoint returns ids only). Hoisted to
// a module constant so the `useOwnerMemberList` query key stays referentially
// stable across renders.
const PARTY_NAME_LOOKUP: ListProjectsParams = {
  page: 1,
  pageSize: 200,
  includeCompleted: true,
  sortBy: "projectCode",
  sortDir: "desc",
};

/** Friendly inline copy for the two PMGT delete conflicts (409). */
function conflictMessage(reason: ProjectsConflictReason | null): string {
  if (reason === "inspection_instruction_linked") {
    return "Can't remove — an inspection instruction is linked.";
  }
  if (reason === "contract_has_positions") {
    return "Can't remove — this park still has turbines.";
  }
  return "Couldn't remove — please try again.";
}

/** Pull the stable conflict reason off a thrown projectsFetch error. */
function reasonOf(err: unknown): ProjectsConflictReason | null {
  const body = (err as ProjectsFetchErrorLike).body;
  const reason = (body as { reason?: string } | null | undefined)?.reason;
  if (
    reason === "inspection_instruction_linked" ||
    reason === "contract_has_positions"
  ) {
    return reason;
  }
  return null;
}

/* ── live parks → turbines (with contractId for park removal) ─────────── */

interface LiveTurbine {
  contractPositionId: string;
  assetId: string;
  assetDisplayLabel: string;
  state: ContractPositionState;
}
interface LivePark {
  siteId: string;
  siteName: string;
  /** The contract that carries this park's positions (for park removal). The
   *  positions list doesn't expose contractId, so we resolve it from the
   *  project's contracts by siteId. */
  contractId: string | null;
  /** PEDIT-PARTIES / FB-43 — the park's contract parties, prefilled on the Parks
   *  step. Resolved from the contracts list by siteId; null when the contract
   *  has no such party or the contract can't be resolved. FB-43 added the
   *  contact ids so a contract party set "address only" prefills in contact
   *  mode + can be edited. */
  contractCustomerTenantId: string | null;
  contractCustomerTenantName: string | null;
  contractCustomerContactId: string | null;
  contractContractorTenantId: string | null;
  contractContractorTenantName: string | null;
  contractContractorContactId: string | null;
  turbines: LiveTurbine[];
}

/** PEDIT-PARTIES / FB-43 — the per-contract party tuple resolved by siteId from
 *  the contracts list, used to prefill the Parks step's contract parties. */
interface ContractPartyInfo {
  contractId: string;
  customerTenantId: string | null;
  customerTenantName: string | null;
  customerContactId: string | null;
  contractorTenantId: string | null;
  contractorTenantName: string | null;
  contractorContactId: string | null;
}

const NO_SITE_KEY = "__no_site__";

function groupLiveParks(
  items: ContractPositionListItem[],
  contractPartiesBySite: Map<string, ContractPartyInfo>,
): LivePark[] {
  const byPark = new Map<string, LivePark>();
  for (const it of items) {
    const key = it.siteId ?? NO_SITE_KEY;
    let node = byPark.get(key);
    if (!node) {
      const parties = it.siteId
        ? (contractPartiesBySite.get(it.siteId) ?? null)
        : null;
      node = {
        siteId: key,
        siteName: it.siteName ?? "Unassigned park",
        contractId: parties?.contractId ?? null,
        contractCustomerTenantId: parties?.customerTenantId ?? null,
        contractCustomerTenantName: parties?.customerTenantName ?? null,
        contractCustomerContactId: parties?.customerContactId ?? null,
        contractContractorTenantId: parties?.contractorTenantId ?? null,
        contractContractorTenantName: parties?.contractorTenantName ?? null,
        contractContractorContactId: parties?.contractorContactId ?? null,
        turbines: [],
      };
      byPark.set(key, node);
    }
    node.turbines.push({
      contractPositionId: it.contractPositionId,
      assetId: it.assetId,
      assetDisplayLabel: it.assetDisplayLabel,
      state: it.state,
    });
  }
  const parks = [...byPark.values()];
  for (const p of parks) {
    p.turbines.sort((a, b) =>
      a.assetDisplayLabel.localeCompare(b.assetDisplayLabel),
    );
  }
  parks.sort((a, b) => a.siteName.localeCompare(b.siteName));
  return parks;
}

export interface EditModeWizardProps {
  projectId: string;
  isOwnerProject: boolean;
  /** DL-01 — see `ProjectWizardProps.accessRole`. */
  accessRole?: string | null | undefined;
}

export function EditModeWizard({
  projectId,
  isOwnerProject,
  accessRole = null,
}: EditModeWizardProps): ReactElement {
  const navigate = useNavigate();
  const { toast } = useToast();

  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantReady =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const queryEnabled = tenantReady && projectId.length > 0;

  // ── load: base fields ──────────────────────────────────────────────
  const detail = useProjectDetailQuery(projectId);
  const updateProject = useUpdateProjectMutation(projectId);
  const tenantSettings = useTenantSettingsQuery();
  const maxAiModels = tenantSettings.data?.maxAiModelsPerProject ?? null;

  const knownTenantsQuery = useKnownTenantsQuery(isOwnerProject);
  const knownTenants = useMemo(
    () => knownTenantsQuery.data?.items ?? [],
    [knownTenantsQuery.data?.items],
  );

  // PEDIT-PARTIES — `GET /projects/{id}` (`ProjectDetail`) carries the party
  // `*_tenant_id`s but NOT their resolved names; the merged own/member list row
  // (`ProjectListItem`) does (`customerTenantName` etc.). Read the same wide
  // sweep the detail page already fires (cached) to resolve the party NAMES so
  // a migrated party tenant — possibly absent from `known-tenants` — still shows
  // its company name.
  const ownerMember = useOwnerMemberList(PARTY_NAME_LOOKUP);
  const listRow = useMemo(
    () => ownerMember.rows.find((r) => r.projectId === projectId) ?? null,
    [ownerMember.rows, projectId],
  );

  // ── load: live parks/turbines ──────────────────────────────────────
  const positionsQuery = useContractPositionsListQuery(
    {
      projectIds: projectId ? [projectId] : [],
      includeCompleted: true,
      page: 1,
      pageSize: POSITIONS_PAGE_SIZE,
      sortBy: "siteName",
      sortDir: "asc",
    },
    { enabled: queryEnabled },
  );
  // Resolve each park's contract id (for park removal) from the project's
  // contracts, keyed by siteId.
  const contractsQuery = useContractsByProjectQuery(
    queryEnabled ? projectId : null,
  );
  const contractIdBySite = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of contractsQuery.data?.items ?? []) {
      if (c.siteId) map.set(c.siteId, c.contractId);
    }
    return map;
  }, [contractsQuery.data?.items]);
  // PEDIT-PARTIES — each contract's parties (id + resolved name) keyed by siteId,
  // for the read-only per-contract prefill on the Parks step.
  const contractPartiesBySite = useMemo(() => {
    const map = new Map<string, ContractPartyInfo>();
    for (const c of contractsQuery.data?.items ?? []) {
      if (!c.siteId) continue;
      map.set(c.siteId, {
        contractId: c.contractId,
        customerTenantId: c.customerTenantId ?? null,
        customerTenantName: c.customerTenantName ?? null,
        customerContactId: c.customerContactId ?? null,
        contractorTenantId: c.contractorTenantId ?? null,
        contractorTenantName: c.contractorTenantName ?? null,
        contractorContactId: c.contractorContactId ?? null,
      });
    }
    return map;
  }, [contractsQuery.data?.items]);
  const liveParks = useMemo(
    () =>
      groupLiveParks(positionsQuery.data?.items ?? [], contractPartiesBySite),
    [positionsQuery.data?.items, contractPartiesBySite],
  );

  // ── Basics step local state (seeded from the detail; re-synced on load) ─
  const [activeStep, setActiveStep] = useState(0);
  const [validated, setValidated] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [offerReference, setOfferReference] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportLanguage, setReportLanguage] = useState("");
  // Track the loaded baseline so Save can send only changed fields + the dirty
  // flag can disable Save until something actually changes.
  const [baseline, setBaseline] = useState<UpdateProjectRequest | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Parties step local state (PEDIT-PARTIES / FB-43) ────────────────
  // Customer/contractor are tenant|contact party values (FB-43 — switchable);
  // reporting stays tenant-only. Seeded from the loaded detail; a separate
  // baseline drives the dirty-check + diff PATCH.
  const [customer, setCustomer] = useState<PartyValue>(EMPTY_PARTY);
  const [contractor, setContractor] = useState<PartyValue>(EMPTY_PARTY);
  const [reportingTenantId, setReportingTenantId] = useState<string | null>(
    null,
  );
  const [partiesBaseline, setPartiesBaseline] = useState<{
    customerTenantId: string | null;
    customerContactId: string | null;
    contractorTenantId: string | null;
    contractorContactId: string | null;
    reportingSubcontractorTenantId: string | null;
  } | null>(null);
  const [partiesError, setPartiesError] = useState<string | null>(null);

  useEffect(() => {
    const d = detail.data;
    if (!d) return;
    const next: UpdateProjectRequest = {
      projectName: d.projectName,
      startDate: d.startDate ?? null,
      endDate: d.endDate ?? null,
      offerReference: d.offerReference ?? null,
      reportLanguage: d.reportLanguage ?? null,
    };
    setProjectName(next.projectName ?? "");
    setStartDate(next.startDate ?? "");
    setEndDate(next.endDate ?? "");
    setOfferReference(next.offerReference ?? "");
    setReportLanguage(next.reportLanguage ?? "");
    setBaseline(next);

    // Seed the party selections + their baseline from the live project. FB-43 —
    // a contact id wins the mode (the party was set "address only"); otherwise
    // tenant mode. Naming a contact never grants; a tenant auto-grants.
    setCustomer(partyValueFromIds(d.customerTenantId, d.customerContactId));
    setContractor(
      partyValueFromIds(d.contractorTenantId, d.contractorContactId),
    );
    setReportingTenantId(d.reportingSubcontractorTenantId ?? null);
    setPartiesBaseline({
      customerTenantId: d.customerTenantId ?? null,
      customerContactId: d.customerContactId ?? null,
      contractorTenantId: d.contractorTenantId ?? null,
      contractorContactId: d.contractorContactId ?? null,
      reportingSubcontractorTenantId: d.reportingSubcontractorTenantId ?? null,
    });
  }, [detail.data]);

  // PEDIT-PARTIES — the prefilled {tenantId, tenantName} pairs the edit pickers
  // show even for tenants absent from `known-tenants` (migrated parties). Names
  // come from the merged list row; the id comes from the detail. `null` when no
  // party tenant is set.
  const customerPrefilled = useMemo<PrefilledTenant | null>(
    () =>
      detail.data?.customerTenantId
        ? {
            tenantId: detail.data.customerTenantId,
            tenantName: listRow?.customerTenantName ?? null,
          }
        : null,
    [detail.data?.customerTenantId, listRow?.customerTenantName],
  );
  const contractorPrefilled = useMemo<PrefilledTenant | null>(
    () =>
      detail.data?.contractorTenantId
        ? {
            tenantId: detail.data.contractorTenantId,
            tenantName: listRow?.contractorTenantName ?? null,
          }
        : null,
    [detail.data?.contractorTenantId, listRow?.contractorTenantName],
  );
  // The reporting subcontractor has no resolved name on the list row, so the
  // picker falls back to the id when the tenant isn't a known link.
  const reportingPrefilled = useMemo<PrefilledTenant | null>(
    () =>
      detail.data?.reportingSubcontractorTenantId
        ? {
            tenantId: detail.data.reportingSubcontractorTenantId,
            tenantName: null,
          }
        : null,
    [detail.data?.reportingSubcontractorTenantId],
  );

  // FB-43 — the effective tenant/contact ids for each party (mutually exclusive
  // by mode). A party in tenant mode carries `*TenantId` + `null` contact; in
  // contact mode `*ContactId` + `null` tenant.
  const customerTenantId =
    customer.mode === "tenant" ? customer.tenantId : null;
  const customerContactId =
    customer.mode === "contact" ? customer.contactId : null;
  const contractorTenantId =
    contractor.mode === "tenant" ? contractor.tenantId : null;
  const contractorContactId =
    contractor.mode === "contact" ? contractor.contactId : null;

  const dirtyParties = useMemo(() => {
    if (!partiesBaseline) return false;
    return (
      partiesBaseline.customerTenantId !== customerTenantId ||
      partiesBaseline.customerContactId !== customerContactId ||
      partiesBaseline.contractorTenantId !== contractorTenantId ||
      partiesBaseline.contractorContactId !== contractorContactId ||
      partiesBaseline.reportingSubcontractorTenantId !== reportingTenantId
    );
  }, [
    partiesBaseline,
    customerTenantId,
    customerContactId,
    contractorTenantId,
    contractorContactId,
    reportingTenantId,
  ]);

  function buildPartiesPatchBody(): UpdateProjectRequest {
    const body: UpdateProjectRequest = {};
    if (!partiesBaseline) return body;
    // FB-43 — when a party's mode changed (tenant→contact / contact→tenant) BOTH
    // ids may differ from the baseline; send the new pair so the BE clears the
    // old slot and sets the new one (and soft-revokes / auto-grants accordingly).
    if (partiesBaseline.customerTenantId !== customerTenantId) {
      body.customerTenantId = customerTenantId;
    }
    if (partiesBaseline.customerContactId !== customerContactId) {
      body.customerContactId = customerContactId;
    }
    if (partiesBaseline.contractorTenantId !== contractorTenantId) {
      body.contractorTenantId = contractorTenantId;
    }
    if (partiesBaseline.contractorContactId !== contractorContactId) {
      body.contractorContactId = contractorContactId;
    }
    if (partiesBaseline.reportingSubcontractorTenantId !== reportingTenantId) {
      body.reportingSubcontractorTenantId = reportingTenantId;
    }
    return body;
  }

  function onSaveParties(): void {
    setPartiesError(null);
    const body = buildPartiesPatchBody();
    if (Object.keys(body).length === 0) return;
    updateProject.mutate(body, {
      onSuccess: () => {
        toast("Project parties saved.", "success");
        // The mutation's onSuccess invalidates the project detail + lists, so
        // the detail re-fetch re-seeds the selections and the merged list
        // re-resolves the prefilled party names — no manual refetch needed.
      },
      onError: () => {
        setPartiesError(
          "Couldn't save the parties — the tenant may not be a linked partner, or you may lack permission.",
        );
      },
    });
  }

  const trimmedName = projectName.trim();

  // Edit validation: only the Basics name is hard-required (parties + parks are
  // managed by their own live controls). Reuse the shared validator with
  // already-satisfied stand-ins so the Basics name error still surfaces.
  const errors = useMemo(
    () =>
      validateWizard({
        trimmedName,
        customer: { mode: "tenant", tenantId: "x", contactId: null },
        contractor: { mode: "tenant", tenantId: "x", contactId: null },
        reporting: { choice: "our_team", tenantId: null },
        parks: [],
        turbines: [],
      }),
    [trimmedName],
  );
  const showErrors = validated;
  const errorSteps = useMemo(
    () => (showErrors ? stepsWithErrors(errors) : new Set<number>()),
    [showErrors, errors],
  );

  // Dirty check for the Basics PATCH.
  const dirtyBasics = useMemo(() => {
    if (!baseline) return false;
    return (
      (baseline.projectName ?? "") !== trimmedName ||
      (baseline.startDate ?? "") !== startDate ||
      (baseline.endDate ?? "") !== endDate ||
      (baseline.offerReference ?? "") !== offerReference.trim() ||
      (baseline.reportLanguage ?? "") !== reportLanguage.trim()
    );
  }, [
    baseline,
    trimmedName,
    startDate,
    endDate,
    offerReference,
    reportLanguage,
  ]);

  function buildPatchBody(): UpdateProjectRequest {
    // Send only changed fields; an empty/blank value clears the nullable field.
    const body: UpdateProjectRequest = {};
    if (!baseline) return body;
    if ((baseline.projectName ?? "") !== trimmedName) {
      body.projectName = trimmedName;
    }
    if ((baseline.startDate ?? "") !== startDate) {
      body.startDate = startDate.length > 0 ? startDate : null;
    }
    if ((baseline.endDate ?? "") !== endDate) {
      body.endDate = endDate.length > 0 ? endDate : null;
    }
    if ((baseline.offerReference ?? "") !== offerReference.trim()) {
      body.offerReference =
        offerReference.trim().length > 0 ? offerReference.trim() : null;
    }
    if ((baseline.reportLanguage ?? "") !== reportLanguage.trim()) {
      body.reportLanguage =
        reportLanguage.trim().length > 0 ? reportLanguage.trim() : null;
    }
    return body;
  }

  function onSaveBasics(): void {
    setSubmitError(null);
    setValidated(true);
    if (trimmedName.length === 0) {
      setSubmitError("Project name is required.");
      return;
    }
    const body = buildPatchBody();
    if (Object.keys(body).length === 0) return;
    updateProject.mutate(body, {
      onSuccess: () => {
        toast("Project saved.", "success");
      },
      onError: () => {
        setSubmitError("Couldn't save the project — please try again.");
      },
    });
  }

  // ── add park/turbine (draft rows committed to the live project) ────────
  const createSite = useCreateSiteMutation();
  const createContract = useCreateContractMutation();
  const createTurbine = useCreateTurbineMutation();
  const createContractPosition = useCreateContractPositionMutation();
  const deletePosition = useDeleteContractPositionMutation();
  const deleteContract = useDeleteContractMutation();
  // FB-43 — edit a contract's parties (tenant|contact) via the new
  // `PATCH /contracts/{contractId}`.
  const updateContract = useUpdateContractMutation();

  /** FB-43 — save one contract's party fields. `body` carries only the changed
   *  parties (the row computes the diff). The mutation invalidates the contracts
   *  list so the prefill re-resolves; the positions tree re-fetches too. */
  function saveContractParties(
    contractId: string,
    body: ContractUpdateRequest,
    onDone: (ok: boolean) => void,
  ): void {
    updateContract.mutate(
      { contractId, body },
      {
        onSuccess: () => {
          toast("Contract parties saved.", "success");
          void contractsQuery.refetch();
          onDone(true);
        },
        onError: () => {
          onDone(false);
        },
      },
    );
  }

  const [draftParks, setDraftParks] = useState<DraftPark[]>([]);
  const [draftTurbines, setDraftTurbines] = useState<DraftTurbine[]>([]);
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);

  function addDraftPark(): void {
    setDraftParks((prev) => [
      ...prev,
      {
        key: nextKey("park"),
        siteName: "",
        countryCode: "",
        isOffshore: false,
      },
    ]);
  }
  function updateDraftPark(key: string, patch: Partial<DraftPark>): void {
    setDraftParks((prev) =>
      prev.map((p) => (p.key === key ? { ...p, ...patch } : p)),
    );
  }
  function removeDraftPark(key: string): void {
    setDraftParks((prev) => prev.filter((p) => p.key !== key));
    setDraftTurbines((prev) =>
      prev.map((t) => (t.parkKey === key ? { ...t, parkKey: "" } : t)),
    );
  }
  function addDraftTurbine(): void {
    setDraftTurbines((prev) => [
      ...prev,
      {
        key: nextKey("turbine"),
        parkKey: draftParks[0]?.key ?? "",
        identifier: "",
        internalParkNumber: "",
        assetTypeId: null,
        assetModelId: null,
        operatorMode: "none",
        operatorTenantId: null,
        operatorContactId: null,
      },
    ]);
  }
  function updateDraftTurbine(next: DraftTurbine): void {
    setDraftTurbines((prev) =>
      prev.map((t) => (t.key === next.key ? next : t)),
    );
  }
  function removeDraftTurbine(key: string): void {
    setDraftTurbines((prev) => prev.filter((t) => t.key !== key));
  }

  const draftErrors = useMemo(
    () =>
      validateWizard({
        trimmedName: "x",
        customer: { mode: "tenant", tenantId: "x", contactId: null },
        contractor: { mode: "tenant", tenantId: "x", contactId: null },
        reporting: { choice: "our_team", tenantId: null },
        parks: draftParks,
        turbines: draftTurbines,
      }),
    [draftParks, draftTurbines],
  );

  async function commitAdditions(): Promise<void> {
    setAddError(null);
    setValidated(true);
    if (
      Object.keys(draftErrors.parks).length > 0 ||
      Object.keys(draftErrors.turbines).length > 0
    ) {
      setAddError("Please fix the highlighted fields before saving.");
      return;
    }
    if (draftParks.length === 0 && draftTurbines.length === 0) return;
    setAddBusy(true);
    try {
      // 1) draft parks → find-or-create site → createContract.
      const siteIdByKey = new Map<string, string>();
      for (const park of draftParks) {
        if (park.existingSiteId !== undefined) {
          siteIdByKey.set(park.key, park.existingSiteId);
          continue;
        }
        const siteBody: CreateSiteBody = { siteName: park.siteName.trim() };
        if (park.countryCode.trim().length > 0) {
          siteBody.countryCode = park.countryCode.trim().toUpperCase();
        }
        if (park.isOffshore) siteBody.isOffshore = true;
        const site = await createSite.mutateAsync(siteBody);
        siteIdByKey.set(park.key, site.id);
      }
      const contractIdByParkKey = new Map<string, string>();
      for (const park of draftParks) {
        const siteId = siteIdByKey.get(park.key);
        if (siteId === undefined) continue;
        const contract = await createContract.mutateAsync({
          projectId,
          siteId,
        });
        contractIdByParkKey.set(park.key, contract.contractId);
      }
      // 2) draft turbines → find-or-create asset → createContractPosition.
      //    A turbine attached to an EXISTING (live) park resolves its contract
      //    from the live contractId map; one attached to a new draft park uses
      //    the freshly-created contract.
      for (const turbine of draftTurbines) {
        const draftContractId = contractIdByParkKey.get(turbine.parkKey);
        // turbine.parkKey may be a draft key OR a live park siteId (the picker
        // offers both — see the add-turbine park select below).
        const liveContractId = contractIdBySite.get(turbine.parkKey);
        const contractId = draftContractId ?? liveContractId;
        if (contractId === undefined) continue;

        let assetId = turbine.existingAssetId;
        if (assetId === undefined) {
          const siteId = siteIdByKey.get(turbine.parkKey) ?? turbine.parkKey; // live siteId
          if (turbine.assetTypeId === null || turbine.assetModelId === null) {
            continue;
          }
          const turbineBody: CreateTurbineBody = {
            siteId,
            assetTypeId: turbine.assetTypeId,
            assetModelId: turbine.assetModelId,
            identifier: turbine.identifier.trim(),
          };
          if (turbine.internalParkNumber.trim().length > 0) {
            turbineBody.internalParkNumber = turbine.internalParkNumber.trim();
          }
          // CM-9 — operator polymorphic + descriptive only (never grants):
          // tenant → `operatorTenantId`, contact → `operatorContactId`.
          if (turbine.operatorMode === "tenant" && turbine.operatorTenantId) {
            turbineBody.operatorTenantId = turbine.operatorTenantId;
          } else if (
            turbine.operatorMode === "contact" &&
            turbine.operatorContactId
          ) {
            turbineBody.operatorContactId = turbine.operatorContactId;
          }
          const asset = await createTurbine.mutateAsync(turbineBody);
          assetId = asset.id;
        }
        await createContractPosition.mutateAsync({ contractId, assetId });
      }
      toast("Parks and turbines added.", "success");
      setDraftParks([]);
      setDraftTurbines([]);
      void positionsQuery.refetch();
      void contractsQuery.refetch();
    } catch (err) {
      const status = (err as ProjectsFetchErrorLike).status;
      setAddError(
        `Couldn't add the parks/turbines${
          typeof status === "number" ? ` (HTTP ${String(status)})` : ""
        }. Please try again.`,
      );
    } finally {
      setAddBusy(false);
    }
  }

  // ── remove park/turbine (confirm + 409) ────────────────────────────
  const [removeTurbineTarget, setRemoveTurbineTarget] =
    useState<LiveTurbine | null>(null);
  const [removeParkTarget, setRemoveParkTarget] = useState<LivePark | null>(
    null,
  );
  const [rowError, setRowError] = useState<{
    id: string;
    message: string;
  } | null>(null);

  function confirmRemoveTurbine(): void {
    const t = removeTurbineTarget;
    if (!t) return;
    setRowError(null);
    deletePosition.mutate(t.contractPositionId, {
      onSuccess: () => {
        toast("Turbine removed.", "success");
      },
      onError: (err) => {
        setRowError({
          id: t.contractPositionId,
          message: conflictMessage(reasonOf(err)),
        });
      },
      onSettled: () => {
        setRemoveTurbineTarget(null);
      },
    });
  }

  function confirmRemovePark(): void {
    const p = removeParkTarget;
    if (!p?.contractId) {
      setRemoveParkTarget(null);
      return;
    }
    setRowError(null);
    deleteContract.mutate(p.contractId, {
      onSuccess: () => {
        toast("Park removed.", "success");
      },
      onError: (err) => {
        setRowError({ id: p.siteId, message: conflictMessage(reasonOf(err)) });
      },
      onSettled: () => {
        setRemoveParkTarget(null);
      },
    });
  }

  // ── render steps ────────────────────────────────────────────────────
  const isOverviewStep = activeStep === EDIT_STEP_LABELS.length - 1;

  // PEDIT-PARTIES — the ad-hoc grant panel collapses below the named parties.
  const [grantsOpen, setGrantsOpen] = useState(false);

  /**
   * PEDIT-PARTIES — the Parties step. PRIMARY content = the named project
   * parties (Customer / Contractor / Reporting), prefilled from the live
   * project and editable (tenant|contact). FB-44 — rendered as the editor for
   * everyone who can edit a project (matching the create parties); tenant-
   * binding stays the BE security boundary (a non-admin tenant-bind 403s,
   * PM-D23). Below: the per-contract (park) parties, also editable. The
   * `AccessGrantPanel` (ad-hoc cross-tenant grants) is retained in a
   * collapsible "Additional access" sub-section.
   */
  function renderPartiesStep(): ReactElement {
    return (
      <Box component="section" aria-label="Project parties">
        <Typography variant="h6" gutterBottom>
          Parties
        </Typography>

        {/* ── Named project parties (primary, prefilled, editable) ─────── */}
        <Stack spacing={3}>
          {/* FB-20 order: Project Customer → Project Contractor →
              Reporting Subcontractor. FB-45 — the contract-scoped parties
              (per park) now follow below on this same Parties step (no longer
              on the Parks step). */}
          <EditPartyField
            kind="customer"
            value={customer}
            prefilled={customerPrefilled}
            onChange={setCustomer}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsQuery.isPending}
          />
          <EditPartyField
            kind="contractor"
            value={contractor}
            prefilled={contractorPrefilled}
            onChange={setContractor}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsQuery.isPending}
          />
          <EditReportingPartyField
            value={reportingTenantId}
            prefilled={reportingPrefilled}
            onChange={setReportingTenantId}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsQuery.isPending}
          />

          {partiesError !== null ? (
            <Alert severity="error" variant="outlined">
              {partiesError}
            </Alert>
          ) : null}

          <Box>
            <Button
              variant="contained"
              disabled={!dirtyParties || updateProject.isPending}
              onClick={onSaveParties}
            >
              {updateProject.isPending ? "Saving…" : "Save parties"}
            </Button>
          </Box>
        </Stack>

        {/* ── Contract parties (per park) — FB-45 ──────────────────────
            Relocated from the Parks & turbines step so ALL parties live
            together on the Parties step (matching the create wizard). Each
            park's contract customer/contractor, prefilled + editable
            (tenant|contact) via `PATCH /contracts/{contractId}`; a park whose
            contract isn't resolved yet keeps the read-only projection. */}
        <Divider sx={{ my: 3 }} />
        <Box component="section" aria-label="Contract parties">
          <Typography variant="subtitle1" gutterBottom>
            Contract parties
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Each park&apos;s contract — customer &amp; contractor.
          </Typography>
          {positionsQuery.isPending ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : liveParks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No parks yet — add parks on the next step.
            </Typography>
          ) : (
            <Stack
              spacing={2}
              divider={<Divider flexItem />}
              data-testid="edit-contract-parties-list"
            >
              {liveParks.map((park) => (
                <Box key={park.siteId}>
                  <Typography variant="subtitle2" gutterBottom>
                    {park.siteName}
                  </Typography>
                  {park.contractId !== null ? (
                    <EditContractParties
                      park={park}
                      contractId={park.contractId}
                      knownTenants={knownTenants}
                      knownTenantsLoading={knownTenantsQuery.isPending}
                      saving={updateContract.isPending}
                      onSave={saveContractParties}
                    />
                  ) : (
                    <Box
                      component="section"
                      aria-label={`Contract parties for ${park.siteName}`}
                      sx={{ pl: 2, pr: 2, py: 1 }}
                      data-testid="edit-contract-parties"
                    >
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={3}
                      >
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Contract customer
                          </Typography>
                          <Typography variant="body2">
                            {park.contractCustomerTenantName ??
                              park.contractCustomerTenantId ??
                              "Not set"}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            Contract contractor
                          </Typography>
                          <Typography variant="body2">
                            {park.contractContractorTenantName ??
                              park.contractContractorTenantId ??
                              "Not set"}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {/* ── Additional access (the ad-hoc grant panel) ──────────────── */}
        <Divider sx={{ my: 3 }} />
        <Box component="section" aria-label="Additional access">
          <ListItemButton
            onClick={() => {
              setGrantsOpen((o) => !o);
            }}
            sx={{ borderRadius: 1, px: 1 }}
            aria-expanded={grantsOpen}
            data-testid="additional-access-toggle"
          >
            {grantsOpen ? (
              <ChevronUpIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
            ) : (
              <ChevronDownIcon
                fontSize="small"
                sx={{ mr: 1, color: "text.secondary" }}
              />
            )}
            <ListItemText
              primary="Additional access"
              secondary="Grant a non-party company (e.g. an on-site subcontractor) and manage existing grants."
              slotProps={{ primary: { variant: "subtitle2" } }}
            />
          </ListItemButton>
          <Collapse in={grantsOpen} timeout="auto" unmountOnExit>
            <Box sx={{ pt: 2 }}>
              <AccessGrantPanel
                projectId={projectId}
                isOwnerProject={isOwnerProject}
              />
            </Box>
          </Collapse>
        </Box>
      </Box>
    );
  }

  function renderStep(): ReactElement | null {
    if (activeStep === 0) {
      return (
        <Stack spacing={3}>
          <BasicsStep
            projectName={projectName}
            onProjectName={setProjectName}
            offerReference={offerReference}
            onOfferReference={setOfferReference}
            reportLanguage={reportLanguage}
            onReportLanguage={setReportLanguage}
            startDate={startDate}
            onStartDate={setStartDate}
            endDate={endDate}
            onEndDate={setEndDate}
            // AI models live in their own PUT-on-save panel below — hide the
            // inline Basics multi-select in edit mode.
            aiModelIds={[]}
            onAiModelIds={() => undefined}
            maxAiModels={maxAiModels}
            showErrors={showErrors}
            errors={errors}
            showAiModels={false}
          />
          {/* PM-WF-01 — the project's workflow, its own PATCH-on-save panel. */}
          <Box component="section" aria-label="Workflow">
            <WorkflowPanel
              projectId={projectId}
              isOwnerProject={isOwnerProject}
            />
          </Box>
          {/* AI models — its own PUT-on-save panel (PM-D38). */}
          <Box component="section" aria-label="AI models">
            <AiModelsPanel
              projectId={projectId}
              isOwnerProject={isOwnerProject}
            />
          </Box>
        </Stack>
      );
    }
    if (activeStep === 1) {
      return renderPartiesStep();
    }
    if (activeStep === 2) {
      return (
        <Box component="section" aria-label="Parks and turbines">
          <Typography variant="h6" gutterBottom>
            Parks &amp; turbines
          </Typography>

          {/* Live parks/turbines with per-row remove. */}
          {positionsQuery.isPending ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : positionsQuery.isError ? (
            <Alert severity="error" variant="outlined">
              Couldn&apos;t load this project&apos;s parks and turbines.
            </Alert>
          ) : liveParks.length === 0 ? (
            <Alert severity="info" variant="outlined">
              No parks or turbines are attached to this project yet.
            </Alert>
          ) : (
            <List
              dense
              disablePadding
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
              data-testid="edit-parks-tree"
            >
              {liveParks.map((park) => (
                <LiveParkRow
                  key={park.siteId}
                  park={park}
                  rowError={rowError}
                  onRemovePark={() => {
                    setRemoveParkTarget(park);
                  }}
                  onRemoveTurbine={(t) => {
                    setRemoveTurbineTarget(t);
                  }}
                />
              ))}
            </List>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Add park/turbine — reuses the create draft rows + FE-1 flow. */}
          <Typography variant="subtitle2" gutterBottom>
            Add parks &amp; turbines
          </Typography>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                New parks
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {draftParks.map((park) => (
                  <ParkRow
                    key={park.key}
                    park={park}
                    error={showErrors ? draftErrors.parks[park.key] : undefined}
                    onChange={(patch) => {
                      updateDraftPark(park.key, patch);
                    }}
                    onRemove={() => {
                      removeDraftPark(park.key);
                    }}
                  />
                ))}
                <Box>
                  <Button
                    size="small"
                    startIcon={<PlusIcon fontSize="small" />}
                    onClick={addDraftPark}
                  >
                    Add park
                  </Button>
                </Box>
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary">
                New turbines
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                {draftTurbines.map((turbine) => (
                  <TurbineRow
                    key={turbine.key}
                    turbine={turbine}
                    parks={draftParks}
                    knownTenants={knownTenants}
                    errors={
                      showErrors ? draftErrors.turbines[turbine.key] : undefined
                    }
                    onChange={updateDraftTurbine}
                    onRemove={() => {
                      removeDraftTurbine(turbine.key);
                    }}
                  />
                ))}
                <Box>
                  <Tooltip
                    title={
                      draftParks.length === 0 ? "Add a new park first." : ""
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        startIcon={<PlusIcon fontSize="small" />}
                        onClick={addDraftTurbine}
                        disabled={draftParks.length === 0}
                      >
                        Add turbine
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Stack>
            </Box>

            {addError !== null ? (
              <Alert severity="error" variant="outlined">
                {addError}
              </Alert>
            ) : null}

            {draftParks.length > 0 || draftTurbines.length > 0 ? (
              <Box>
                <Button
                  variant="contained"
                  disabled={addBusy}
                  onClick={() => {
                    void commitAdditions();
                  }}
                >
                  {addBusy ? "Saving…" : "Save additions"}
                </Button>
              </Box>
            ) : null}
          </Stack>
        </Box>
      );
    }
    // Overview = Review step: status-coloured map + entity tree (live project).
    // FB-37 — the Project map section renders FIRST; PEDIT-TREE then replaces
    // the old parks→turbines list (which duplicated the Parks & Turbines step)
    // with the full read-only project ENTITY tree (Project → Contract →
    // Position → Inspection Instruction → Data Acquisition).
    return (
      <Stack spacing={3} aria-label="Overview">
        <Box component="section" aria-label="Project map">
          <Typography variant="h6" gutterBottom>
            Map
          </Typography>
          <Suspense
            fallback={
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            }
          >
            {/* PMGT FE edit-UI polish — a taller Overview map (≈2× the 360
                default) so an interested user gets a good portion of the
                screen; an enlarge affordance (top-right) opens it full-size. */}
            <ProjectMap projectId={projectId} height={720} />
          </Suspense>
        </Box>
        <Box component="section" aria-label="Project structure">
          <Typography variant="h6" gutterBottom>
            Project structure
          </Typography>
          <ProjectEntityTree
            projectId={projectId}
            ownership={isOwnerProject ? "owner" : "member"}
            accessRole={accessRole}
          />
        </Box>
      </Stack>
    );
  }

  if (detail.isPending && isOwnerProject) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    // `overflowY: "auto"` here silently promotes `overflow-x` to `auto` (CSS
    // spec), making THIS box its own horizontal scroll container — a descendant
    // of `<main>`, so the app-shell `overflowX: "clip"` (FB-29) can't reach it.
    // That's the persistent edit-wizard X-scrollbar on steps 1–3. Clip X here too
    // so the wizard scrolls vertically only.
    <Box sx={{ overflowY: "auto", overflowX: "clip" }}>
      <WizardShell
        stepLabels={EDIT_STEP_LABELS}
        activeStep={activeStep}
        onStepChange={setActiveStep}
        errorSteps={errorSteps}
        showErrorBanner={showErrors && hasAnyError(errors)}
        submitError={submitError}
        // Only the Basics step has a terminal Save (PATCH base fields). Parties
        // / AI / add / remove all save via their own controls inline; the
        // Overview step is read-only.
        primaryLabel={updateProject.isPending ? "Saving…" : "Save"}
        showPrimaryAction={activeStep === 0}
        primaryDisabled={!dirtyBasics || updateProject.isPending}
        onPrimary={onSaveBasics}
        onBack={() => {
          setActiveStep((s) => Math.max(0, s - 1));
        }}
        onNext={() => {
          setActiveStep((s) => Math.min(EDIT_STEP_LABELS.length - 1, s + 1));
        }}
        onCancel={() => {
          void navigate("/projects");
        }}
        showNext={!isOverviewStep}
      >
        {renderStep()}
      </WizardShell>

      <ConfirmDialog
        open={removeTurbineTarget !== null}
        title="Remove turbine?"
        description={
          removeTurbineTarget
            ? `Remove ${removeTurbineTarget.assetDisplayLabel} from this project? This soft-deletes the turbine's position.`
            : ""
        }
        confirmLabel="Remove"
        danger
        pending={deletePosition.isPending}
        onConfirm={confirmRemoveTurbine}
        onCancel={() => {
          setRemoveTurbineTarget(null);
        }}
      />
      <ConfirmDialog
        open={removeParkTarget !== null}
        title="Remove park?"
        description={
          removeParkTarget
            ? `Remove ${removeParkTarget.siteName} from this project? A park can only be removed once its turbines are gone.`
            : ""
        }
        confirmLabel="Remove"
        danger
        pending={deleteContract.isPending}
        onConfirm={confirmRemovePark}
        onCancel={() => {
          setRemoveParkTarget(null);
        }}
      />
    </Box>
  );
}

/* ── editable per-contract parties (FB-43) ───────────────────────────── */

/**
 * FB-43 — the per-park (per-contract) parties editor on the project-edit Parks
 * step. Prefilled from the live contract (tenant OR contact, mode follows
 * whichever is set) and editable with the SAME tenant|contact toggle as the
 * create flow. Save → `PATCH /contracts/{contractId}` with only the changed
 * parties (mutually-exclusive tenant/contact ids per party). The current party
 * tenant is always an option (migrated parties show their NAME). Owner-
 * `tenant_admin`-gated by the caller (PM-D23).
 */
function EditContractParties({
  park,
  contractId,
  knownTenants,
  knownTenantsLoading,
  saving,
  onSave,
}: {
  park: LivePark;
  contractId: string;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  saving: boolean;
  onSave: (
    contractId: string,
    body: ContractUpdateRequest,
    onDone: (ok: boolean) => void,
  ) => void;
}): ReactElement {
  const seededCustomer = useMemo(
    () =>
      contractPartyFromIds(
        park.contractCustomerTenantId,
        park.contractCustomerContactId,
      ),
    [park.contractCustomerTenantId, park.contractCustomerContactId],
  );
  const seededContractor = useMemo(
    () =>
      contractPartyFromIds(
        park.contractContractorTenantId,
        park.contractContractorContactId,
      ),
    [park.contractContractorTenantId, park.contractContractorContactId],
  );

  const [customer, setCustomer] = useState<ContractPartyValue>(seededCustomer);
  const [contractor, setContractor] =
    useState<ContractPartyValue>(seededContractor);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Re-seed when the live contract data changes (e.g. after a save refetch).
  useEffect(() => {
    setCustomer(seededCustomer);
    setContractor(seededContractor);
  }, [seededCustomer, seededContractor]);

  // Prefilled {id, name} so a migrated contract tenant shows its NAME even when
  // it isn't a current known link.
  const customerPrefilled: PrefilledTenant | null =
    park.contractCustomerTenantId
      ? {
          tenantId: park.contractCustomerTenantId,
          tenantName: park.contractCustomerTenantName,
        }
      : null;
  const contractorPrefilled: PrefilledTenant | null =
    park.contractContractorTenantId
      ? {
          tenantId: park.contractContractorTenantId,
          tenantName: park.contractContractorTenantName,
        }
      : null;

  const custTenantId = customer.mode === "tenant" ? customer.tenantId : null;
  const custContactId = customer.mode === "contact" ? customer.contactId : null;
  const ctrTenantId = contractor.mode === "tenant" ? contractor.tenantId : null;
  const ctrContactId =
    contractor.mode === "contact" ? contractor.contactId : null;

  const dirty =
    custTenantId !== park.contractCustomerTenantId ||
    custContactId !== park.contractCustomerContactId ||
    ctrTenantId !== park.contractContractorTenantId ||
    ctrContactId !== park.contractContractorContactId;

  function buildBody(): ContractUpdateRequest {
    const body: ContractUpdateRequest = {};
    if (custTenantId !== park.contractCustomerTenantId) {
      body.customerTenantId = custTenantId;
    }
    if (custContactId !== park.contractCustomerContactId) {
      body.customerContactId = custContactId;
    }
    if (ctrTenantId !== park.contractContractorTenantId) {
      body.contractorTenantId = ctrTenantId;
    }
    if (ctrContactId !== park.contractContractorContactId) {
      body.contractorContactId = ctrContactId;
    }
    return body;
  }

  function handleSave(): void {
    setSaveError(null);
    const body = buildBody();
    if (Object.keys(body).length === 0) return;
    onSave(contractId, body, (ok) => {
      if (!ok) {
        setSaveError(
          "Couldn't save the contract parties — the tenant may not be a linked partner, or you may lack permission.",
        );
      }
    });
  }

  return (
    <Box
      component="section"
      aria-label={`Contract parties for ${park.siteName}`}
      sx={{
        pl: 5,
        pr: 2,
        py: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
      data-testid="edit-contract-parties"
    >
      <Typography variant="caption" color="text.secondary">
        Contract parties — customer / contractor for this park&apos;s contract.
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={3}
        alignItems="flex-start"
        useFlexGap
        sx={{ flexWrap: { sm: "wrap" } }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Contract customer
          </Typography>
          <ContractPartyField
            kind="contractCustomer"
            value={customer}
            onChange={setCustomer}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
            prefilled={customerPrefilled}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Contract contractor
          </Typography>
          <ContractPartyField
            kind="contractContractor"
            value={contractor}
            onChange={setContractor}
            knownTenants={knownTenants}
            knownTenantsLoading={knownTenantsLoading}
            prefilled={contractorPrefilled}
          />
        </Box>
      </Stack>
      {saveError !== null ? (
        <Alert severity="error" variant="outlined" sx={{ maxWidth: 480 }}>
          {saveError}
        </Alert>
      ) : null}
      <Box>
        <Button
          size="small"
          variant="contained"
          disabled={!dirty || saving}
          onClick={handleSave}
        >
          {saving ? "Saving…" : "Save contract parties"}
        </Button>
      </Box>
    </Box>
  );
}

/* ── live park row (collapsible, removable) ──────────────────────────── */

function LiveParkRow({
  park,
  rowError,
  onRemovePark,
  onRemoveTurbine,
}: {
  park: LivePark;
  rowError: { id: string; message: string } | null;
  onRemovePark: () => void;
  onRemoveTurbine: (t: LiveTurbine) => void;
}): ReactElement {
  const [open, setOpen] = useState(true);
  const parkError = rowError?.id === park.siteId ? rowError.message : null;
  return (
    <Box component="li" sx={{ listStyle: "none" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pr: 1,
        }}
      >
        <ListItemButton
          onClick={() => {
            setOpen((o) => !o);
          }}
          sx={{ py: 0.5, flex: 1 }}
          aria-expanded={open}
          data-testid="edit-park-row"
        >
          {open ? (
            <ChevronUpIcon
              fontSize="small"
              sx={{ mr: 1, color: "text.secondary" }}
            />
          ) : (
            <ChevronDownIcon
              fontSize="small"
              sx={{ mr: 1, color: "text.secondary" }}
            />
          )}
          <ListItemText
            primary={park.siteName}
            slotProps={{ primary: { variant: "subtitle2" } }}
          />
          <Chip
            size="small"
            variant="outlined"
            label={`${String(park.turbines.length)} ${
              park.turbines.length === 1 ? "turbine" : "turbines"
            }`}
          />
        </ListItemButton>
        <Tooltip title="Remove park">
          <span>
            <IconButton
              size="small"
              color="error"
              aria-label={`Remove park ${park.siteName}`}
              disabled={park.contractId === null}
              onClick={onRemovePark}
            >
              <Trash2Icon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      {parkError ? (
        <Alert
          severity="warning"
          variant="outlined"
          sx={{ mx: 2, mb: 1 }}
          data-testid="edit-park-error"
        >
          {parkError}
        </Alert>
      ) : null}
      <Collapse in={open} timeout="auto" unmountOnExit>
        {/* FB-45 — the per-park contract parties moved to the Parties step
            (page 2); this step stays focused on the parks → turbines tree
            (add / remove). */}
        <List dense disablePadding component="ul">
          {park.turbines.map((t) => {
            const tError =
              rowError?.id === t.contractPositionId ? rowError.message : null;
            return (
              <Box key={t.contractPositionId} component="li">
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    pl: 5,
                    pr: 1,
                    py: 0.5,
                  }}
                  data-testid="edit-turbine-row"
                >
                  <Typography
                    variant="body2"
                    noWrap
                    title={t.assetDisplayLabel}
                    sx={{ flex: 1 }}
                  >
                    {t.assetDisplayLabel}
                  </Typography>
                  <ContractPositionStatusChip state={t.state} />
                  <Tooltip title="Remove turbine">
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Remove turbine ${t.assetDisplayLabel}`}
                      onClick={() => {
                        onRemoveTurbine(t);
                      }}
                    >
                      <Trash2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                {tError ? (
                  <Alert
                    severity="warning"
                    variant="outlined"
                    sx={{ mx: 2, mb: 1 }}
                    data-testid="edit-turbine-error"
                  >
                    {tError}
                  </Alert>
                ) : null}
              </Box>
            );
          })}
        </List>
      </Collapse>
    </Box>
  );
}
